import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Card, 
    TableContainer, 
    Table, 
    TableBody, 
    TablePagination, 
    Pagination, 
    TextField, 
    InputAdornment, 
    IconButton,
    Chip
} from '@mui/material';
import { 
    Search as SearchIcon, 
    Clear as ClearIcon, 
    VisibilityOutlined as VisibilityOutlinedIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTableState } from 'hooks/useTableState';
import { getAllServicesIaBalanceStore } from 'store/services-ia-balance/thunks';
import { useResponseHandler } from 'hooks/useResponseHandler';
import { hasPemissions } from 'commons/utils';
import { formatCredits, formatCreditConsumption, usarFuncionesComposicion, corregirTexto, firstLetterUpperCase } from 'commons/utils/helpers';
import { paddingCardsTables } from 'commons/values';

import { GenericTableHead, HeadCell } from 'commons/generics/GenericTableHead';
import { StyledTableRow, StyledTableCell } from 'commons/generics/StyledTableComponents';
import { SinDatos } from 'commons/generics/SinDatos';

const headCells: HeadCell[] = [
    { id: 'cliente', label: 'Cliente' },
    { id: 'plan', label: 'Plan' },
    { id: 'availableCredits', label: 'Saldo.' },
    { id: 'reservedCredits', label: 'Reservados' },
    { id: 'consumedTotal', label: 'Consumidos' },
    { id: 'actions', label: 'Acciones', align: 'center' }
];

export const TableBalanceIa = () => {
    const dispatch: any = useDispatch();
    const navigate = useNavigate();
    const { balance_list } = useSelector((state: any) => state.servicesIaBalance);
    const { handleResponse, ResponseComponents } = useResponseHandler();

    const {
        dataTableList,
        canView, setCanView,
        searchTerm, setSearchTerm,
        order, orderBy,
        handleSearch: handleSearchHook,
        handleResetSearch: handleResetSearchHook,
        page, rowsPerPage,
        handleChangePage, handleChangeRowsPerPage,
        pagePagination, itemCountPagination,
        changePaginationPage: changePaginationPageHook,
        handleRequestSort,
        updatePaginationFromList,
        getSortedAndFilteredList,
        isServerSide
    } = useTableState('balance_list');

    const [inputValue, setInputValue] = useState('');

    const checkPermissions = async () => {
        const view = await hasPemissions('modulo.manager', 'modulo.manager.plataforma.ia', 'view', 'modulo.manager.plataforma.ia.creditos', 'modulo.manager.plataforma.ia.creditos.balance');
        setCanView(view);
    };

    useEffect(() => {
        checkPermissions();
    }, []);

    const getInitialData = async () => {
        console.log('pompitototo')
        const response = await dispatch(getAllServicesIaBalanceStore({ page: 1, take: rowsPerPage, param: inputValue }));
        await handleResponse(response, 'obtener balances', false);
    };

    useEffect(() => {
        if (canView) {
            getInitialData();
        }
    }, [canView]);

    useEffect(() => {
        updatePaginationFromList(balance_list);
    }, [balance_list, updatePaginationFromList]);

    const handleSearch = async (param: string) => {
        setSearchTerm(param);
        setInputValue(param);
        handleSearchHook(param, dispatch, (p: string) => getAllServicesIaBalanceStore({ page: 1, take: rowsPerPage, param: p }), getInitialData, null, 'buscar balances');
    };

    const onResetSearch = () => {
        setInputValue('');
        handleResetSearchHook(() => getInitialData());
    };

    const changePaginationPage = async (event: React.ChangeEvent<unknown>, value: number) => {
        changePaginationPageHook(event, value);
        const response = await dispatch(getAllServicesIaBalanceStore({ page: value, take: rowsPerPage, param: searchTerm }));
        await handleResponse(response, 'listar balances', false);
    };

    const handleView = (row: any) => {
        navigate(`/manager/plataforma-ia/creditos/balance/${row.id}/detalle`);
    };

    const sortedList = getSortedAndFilteredList(
        dataTableList || [], 
        ['customer.name'],
        'status', 
        1
    );

    return (
        <Box sx={{ width: '100%', mt: paddingCardsTables }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
                <TextField
                    placeholder="Buscar..."
                    variant="outlined"
                    size="small"
                    autoComplete='off'
                    value={inputValue}
                    onChange={(e) => handleSearch(e.target.value)}
                    sx={{ width: 400 }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                        endAdornment: inputValue && (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={onResetSearch}><ClearIcon /></IconButton>
                            </InputAdornment>
                        )
                    }}
                />
            </Box>

            <Card sx={{ borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none', overflow: 'hidden' }}>
                <TableContainer>
                    <Table>
                        <GenericTableHead 
                            headCells={headCells} 
                            order={order} 
                            orderBy={orderBy} 
                            onRequestSort={handleRequestSort} 
                        />
                        <TableBody>
                            {sortedList.length === 0 ? (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={headCells.length} align="center">
                                        <SinDatos />
                                    </StyledTableCell>
                                </StyledTableRow>
                            ) : (
                                sortedList
                                    .slice(isServerSide ? 0 : page * rowsPerPage, isServerSide ? rowsPerPage : page * rowsPerPage + rowsPerPage)
                                    .map((row: any) => (
                                        <StyledTableRow key={row.id}>
                                            <StyledTableCell>
                                                <Box sx={{ fontWeight: 600, color: 'text.primary' }}>
                                                    {usarFuncionesComposicion(corregirTexto, firstLetterUpperCase, row.customer?.name || row.customer?.email || 'No definido')}
                                                </Box>
                                            </StyledTableCell>

                                            <StyledTableCell>
                                                {usarFuncionesComposicion(corregirTexto, firstLetterUpperCase, row.plan?.name || 'No definido')}
                                            </StyledTableCell>
                                            
                                            <StyledTableCell>
                                                <Chip
                                                    label={formatCredits(row.availableCredits)}
                                                    color="success"
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </StyledTableCell>

                                            <StyledTableCell>
                                                {formatCredits(row.reservedCredits)}
                                            </StyledTableCell>
                                            
                                            <StyledTableCell>
                                                {formatCreditConsumption(row.consumedTotal)}
                                            </StyledTableCell>
                                            
                                            <StyledTableCell align="center">
                                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                                    <span style={{ cursor: !canView ? 'not-allowed' : 'pointer' }}>
                                                        <IconButton 
                                                            size="small" 
                                                            onClick={() => canView && handleView(row)} 
                                                            disabled={!canView} 
                                                            color="info"
                                                        >
                                                            <VisibilityOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Box>
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {dataTableList.length > 0 && (
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={dataTableList.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage="Filas por página"
                    />
                )}

                {dataTableList.length > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, padding: 2.5, borderTop: 1, borderColor: 'divider' }}>
                        <Pagination
                            sx={{ justifyContent: 'center' }}
                            color="primary"
                            count={itemCountPagination}
                            page={pagePagination}
                            onChange={changePaginationPage} 
                            showFirstButton 
                            showLastButton
                            shape="circular"
                        />
                    </Box>
                )}
            </Card>

            {ResponseComponents}
        </Box>
    );
};
